import * as React from 'react'
import { FC, useCallback, useMemo } from 'react'
import { Button, Flex, Pagination, Table } from 'antd'
import PlusOutlined from '@ant-design/icons/lib/icons/PlusOutlined'
import { useSearchParams } from 'react-router-dom'
import TableWrapper from '../../../../../components/table-wrapper/table-wrapper'
import FormModal from '../../../../../components/form-modal/form-modal'
import CreateReceiptForm from './components/create-receipt-form/create-receipt-form'
import { columns } from './constants/columns'
import useEndpointQueryParams, {
    DeserializeFilters,
} from '../../../../../hooks/table-pages/use-endpoint-query-parameters'
import { EndpointQueryParameters } from '../../../../../types/strapi/strapi'
import useTablePagination from '../../../../../hooks/table-pages/use-table-pagination'
import Receipt from '../../../../../types/api/receipt'
import { useGetMyReceiptsQuery } from '../../../../../store/api/receipts-api'
import ReceiptPosition from '../../../../../types/api/receipt-position'
import FiltersForm from './components/filters-form/filters-form'
import qs from 'qs'
import FormValues from './components/filters-form/types/form-values'
import { buildFilters } from './helpers/build-filters'
import filterReceiptPositionsByForm from '../../../../../helpers/filters/filter-receipt-positions-by-form'

export type DataSourceItem = ReceiptPosition & { rowSpan: number; key: string }

const deserializeFilters: DeserializeFilters = filtersString => {
    const formValues = qs.parse(filtersString) as Partial<FormValues>
    return buildFilters(formValues)
}

interface ReceiptsProps {}

const Receipts: FC<ReceiptsProps> = () => {
    const [searchParams] = useSearchParams()
    const params = useEndpointQueryParams({ deserializeFilters })
    const receiptsParams: EndpointQueryParameters = {
        populate: {
            supplier: true,
            receipt_positions: {
                populate: {
                    material: { populate: '*' },
                    initiator: true,
                    unit: true,
                    order_request: { populate: { initiator: true } },
                },
            },
        },
        pagination: params.pagination,
        filters: params.filters,
        sort: ['createdAt:desc'],
    }

    const { data, isLoading, refetch } = useGetMyReceiptsQuery(receiptsParams)
    const pagination = useTablePagination(params, data)
    const receipts: Receipt[] = data?.data || []

    const formValues = useMemo(
        () => qs.parse(searchParams.get('filters') || '') as Partial<FormValues>,
        [searchParams]
    )

    const dataSource: DataSourceItem[] = receipts.flatMap(s => {
        const positions = filterReceiptPositionsByForm(s.receipt_positions || [], formValues)
        if (positions.length === 0) {
            return []
        }

        return positions.map((pos, index) => ({
            ...pos,
            key: `${s.id}-${pos.id}`,
            receiptCreatedAt: s.createdAt,
            receiptId: s.id,
            receiptSum: s.sum,
            receiptDocumentId: s.documentId,
            receiptEstimatedDate: s.estimatedDate,
            supplier: s.supplier,
            receiptStatus: s.receiptStatus,
            rowSpan: index === 0 ? positions.length : 0,
        }))
    })

    const tableFooter = useCallback(
        () => <Pagination {...pagination} size='small' align='end' />,
        [pagination]
    )

    return (
        <>
            <Flex style={{ flex: '0 0 auto', marginBottom: '16px' }} justify={'end'}>
                <FormModal
                    title={'Создание заказа'}
                    width={1200}
                    renderOpener={onOpen => (
                        <Button
                            type={'primary'}
                            size={'middle'}
                            icon={<PlusOutlined />}
                            onClick={onOpen}
                        >
                            Создать заказ
                        </Button>
                    )}
                    renderContent={onClose => (
                        <CreateReceiptForm
                            onSuccess={() => {
                                refetch()
                                onClose()
                            }}
                        />
                    )}
                />
            </Flex>
            <TableWrapper style={{ flex: 'auto' }} currentTableHeight={75}>
                <Table
                    bordered
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    footer={tableFooter}
                    loading={isLoading}
                    rowKey={'documentId'}
                    summary={() => (
                        <Table.Summary fixed={'top'}>
                            <FiltersForm />
                        </Table.Summary>
                    )}
                />
            </TableWrapper>
        </>
    )
}

export default Receipts
